import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_FIRST_ATTEMPT_TIMEOUT_MS = 12000;
const AUTH_WAKE_RETRY_TIMEOUT_MS = 30000;

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// The access_token cookie expires after 15 minutes (see backend/security.py),
// but nothing was ever wired up to use the 7-day refresh_token the backend
// already issues alongside it — every page across the app was silently
// getting logged out 15 minutes after load/login, surfacing as a raw
// "Not authenticated" 401 on whatever protected call happened to run next.
//
// This interceptor is registered once at module load (every page uses the
// bare `axios` import directly, not a shared client, so it has to live on
// the global instance to cover all of them) and transparently retries a
// 401'd request once after calling /auth/refresh. If the refresh itself
// fails (refresh token also expired, or the user was never logged in),
// `authFailureHandler` — wired up by AuthProvider below — flips the app's
// user state to false so ProtectedRoute redirects to /login instead of
// leaving a stale, half-logged-in page on screen.
let authFailureHandler = null;
let refreshInFlight = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isTemporaryServerError(error) {
  const status = error.response?.status;
  return (
    error.code === 'ECONNABORTED'
    || error.code === 'ERR_NETWORK'
    || !error.response
    || status === 502
    || status === 503
    || status === 504
  );
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const url = original?.url || '';
    const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register');

    if (error.response?.status !== 401 || !original || original._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }
    original._retried = true;

    try {
      if (!refreshInFlight) {
        refreshInFlight = axios
          .post(`${API}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => { refreshInFlight = null; });
      }
      await refreshInFlight;
      return axios(original);
    } catch {
      authFailureHandler?.();
      return Promise.reject(error);
    }
  },
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverWaking, setServerWaking] = useState(false);
  const [authError, setAuthError] = useState(false);

  const checkAuth = useCallback(async () => {
    setLoading(true);
    setAuthError(false);
    const wakingTimer = window.setTimeout(() => setServerWaking(true), 1500);

    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const { data } = await axios.get(`${API}/auth/me`, {
            withCredentials: true,
            timeout: attempt === 0 ? AUTH_FIRST_ATTEMPT_TIMEOUT_MS : AUTH_WAKE_RETRY_TIMEOUT_MS,
          });
          setUser(data);
          return data;
        } catch (error) {
          if (attempt === 0 && isTemporaryServerError(error)) {
            // Render's free instances sleep between visits. The timed-out
            // request still wakes the service, so a single safe GET retry is
            // normally enough once the container is ready.
            setServerWaking(true);
            await wait(750);
            continue;
          }

          if (error.response?.status === 401 || error.response?.status === 403) {
            setUser(false);
          } else {
            // Do not treat a sleeping/unavailable backend as a logout. A
            // protected route can now offer Retry instead of bouncing the
            // member to the login screen.
            setAuthError(true);
          }
          return false;
        }
      }
      return false;
    } finally {
      window.clearTimeout(wakingTimer);
      setServerWaking(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Lets the module-level interceptor above flip `user` to false when a
  // token refresh definitively fails, instead of leaving stale UI state.
  useEffect(() => {
    authFailureHandler = () => setUser(false);
    return () => { authFailureHandler = null; };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      if (!e.response) {
        return { success: false, error: "Can't reach the server. Check your connection and try again." };
      }
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, referralCode) => {
    try {
      const payload = { email, password, name };
      if (referralCode) payload.referral_code = referralCode;
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, serverWaking, authError, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
